import React, { useState } from 'react';
import { AccountAttributes } from '@loyalty/shared-common/gamification-api-client';
import { Button, Menu, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { createStyles, makeStyles } from '@mui/styles';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { useTranslation } from 'react-i18next';
import { useAccounts } from '../../accounts/AccountsContext';

interface StyleProps {
  anchorEl?: HTMLButtonElement;
}

const useStyles = makeStyles(() =>
  createStyles<any, StyleProps>({
    root: {
      display: 'flex',
      borderLeft: `1px solid ${colorPalette.grey.main}`,
    },
    button: {
      height: '100% !important',
    },
    menu: {
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
      marginLeft: 1,
      minWidth: ({ anchorEl }) => anchorEl && anchorEl.getBoundingClientRect()?.width + 2,
    },
    menuList: {
      padding: 0,
    },
    warningIcon: {
      marginLeft: '10px',
    },
  }),
);

interface Props {
  onChange?: (account: AccountAttributes) => void;
}

export function AccountSwitcher({ onChange }: Props): React.ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);
  const classes = useStyles({ anchorEl });
  const { accounts, currentAccount } = useAccounts();
  const { t } = useTranslation();

  const handleClick = ({ currentTarget }: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const handleChange = (account: AccountAttributes) => () => {
    handleClose();
    onChange?.(account);
  };

  return (
    <div className={classes.root}>
      <Button
        className={classes.button}
        variant="text"
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        onClick={handleClick}
      >
        {currentAccount?.name}
      </Button>
      <Menu
        classes={{ paper: classes.menu, list: classes.menuList }}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {accounts
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((account: AccountAttributes) => (
            <MenuItem
              key={account.id}
              onClick={handleChange(account)}
              selected={currentAccount?.id === account.id}
            >
              {account.name}
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
}
